import { backgroundOrange, orange } from '@/theme/colors'
import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal'
import Modal from '@components/Modal'
import { useDeleteTrip, useLeaveTrip } from '@hooks'
import { useUpdateTrip } from '@hooks/useUpdateTrip'
import { ButtonComponent, ButtonGroupComponent } from '@pages/Itinerary/AddEventModal/FormComponents'
import { isLeaveBlockedByBalances } from '@lib/apiError'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import TripForm from '../CreateTripModal/TripForm'
import {
	Divider,
	ModalFooter,
	SectionLabel,
	ToggleInput,
	ToggleSlider,
	ToggleWrapper,
	VisibilityDescription,
	VisibilityRow,
	VisibilityStatus,
	VisibilityText,
	VisibilityTitle,
} from './EditTripModal.styles'

interface EditTripModalProps {
	isOpen: boolean
	onClose: () => void
	tripId: string
	initialName: string
	initialCurrencyCode: string
	initialIsVisibleToPublic: boolean
	isOwner?: boolean
}

const EditTripModal: React.FC<EditTripModalProps> = ({
	isOpen,
	onClose,
	tripId,
	initialName,
	initialCurrencyCode,
	initialIsVisibleToPublic,
	isOwner,
}) => {
	const [name, setName] = useState(initialName)
	const [currencyCode, setCurrencyCode] = useState(initialCurrencyCode)
	const [isVisibleToPublic, setIsVisibleToPublic] = useState(initialIsVisibleToPublic)
	const [isTogglingVisibility, setIsTogglingVisibility] = useState(false)
	const updateTrip = useUpdateTrip(tripId)
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const navigate = useNavigate()
	const deleteMutation = useDeleteTrip()
	const leaveMutation = useLeaveTrip()

	useEffect(() => {
		if (isOpen) {
			setName(initialName)
			setCurrencyCode(initialCurrencyCode)
			setIsVisibleToPublic(initialIsVisibleToPublic)
		}
	}, [isOpen, initialName, initialCurrencyCode, initialIsVisibleToPublic])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!name.trim()) return

		try {
			await updateTrip.mutateAsync({
				name: name.trim(),
				defaultCurrencyCode: currencyCode,
			})
			toast.success('Trip settings updated successfully!')
			onClose()
		} catch (error) {
			toast.error('Failed to update trip settings. Please try again.')
			console.error('Error updating trip:', error)
		}
	}

	const handleCancel = () => {
		setName(initialName)
		setCurrencyCode(initialCurrencyCode)
		setIsVisibleToPublic(initialIsVisibleToPublic)
		onClose()
	}

	const handleVisibilityToggle = async () => {
		if (isTogglingVisibility) return
		const newValue = !isVisibleToPublic
		setIsVisibleToPublic(newValue)
		setIsTogglingVisibility(true)
		try {
			await updateTrip.mutateAsync({ isVisibleToPublic: newValue })
			toast.success(newValue ? 'Itinerary is now public' : 'Itinerary is now private')
		} catch (error) {
			setIsVisibleToPublic(!newValue)
			toast.error('Failed to update visibility. Please try again.')
			console.error('Error updating visibility:', error)
		} finally {
			setIsTogglingVisibility(false)
		}
	}

	const handleDeleteClick = () => {
		setIsConfirmOpen(true)
	}

	const handleConfirmDelete = async () => {
		if (typeof isOwner === 'undefined') {
			setIsConfirmOpen(false)
			return
		}

		if (!isOwner) {
			leaveMutation.mutate(
				{ tripId },
				{
					onSuccess: () => {
						toast.success('Left trip')
						onClose()
						navigate('/trips')
					},
					onError: error => {
						console.error('Failed to leave trip', error)
						if (isLeaveBlockedByBalances(error)) {
							toast.error('Please delete or remove yourself from all expenses and pay ups first.')
						} else {
							toast.error('Failed to leave trip. Please try again.')
						}
					},
				},
			)
		} else {
			deleteMutation.mutate(
				{ tripId },
				{
					onSuccess: () => {
						toast.success('Trip deleted')
						onClose()
						navigate('/trips')
					},
					onError: error => {
						console.error('Failed to delete trip', error)
						toast.error('Failed to delete trip. Please try again.')
					},
				},
			)
		}

		setIsConfirmOpen(false)
	}

	const formId = 'edit-trip-form'
	const leftAction =
		typeof isOwner === 'undefined' ? null : (
			<ButtonComponent variant="danger" type="button" onClick={handleDeleteClick}>
				{!isOwner ? 'Leave trip' : 'Delete trip'}
			</ButtonComponent>
		)

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			title="Trip Settings"
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			closeButtonColor={orange}
			footer={
				<ModalFooter>
					<div>{leftAction}</div>
					<ButtonGroupComponent>
						<ButtonComponent type="button" variant="secondary" onClick={handleCancel}>
							Cancel
						</ButtonComponent>
						<ButtonComponent type="submit" form={formId} variant="primary" disabled={!name.trim() || updateTrip.isPending}>
							Save Changes
						</ButtonComponent>
					</ButtonGroupComponent>
				</ModalFooter>
			}
		>
			<TripForm
				formId={formId}
				name={name}
				currencyCode={currencyCode}
				onNameChange={setName}
				onCurrencyChange={setCurrencyCode}
				onSubmit={handleSubmit}
			/>

			<Divider />

			<SectionLabel>Itinerary Visibility</SectionLabel>

			<VisibilityRow>
				<VisibilityText>
					<VisibilityTitle>Public Itinerary</VisibilityTitle>
					<VisibilityDescription>
						Anyone with the share link can view your itinerary — no Orbit account required.
					</VisibilityDescription>
					<VisibilityStatus $isPublic={isVisibleToPublic}>
						{isTogglingVisibility ? 'Saving…' : isVisibleToPublic ? '● Public' : '○ Private'}
					</VisibilityStatus>
				</VisibilityText>
				<ToggleWrapper>
					<ToggleInput
						type="checkbox"
						id="visibility-toggle"
						checked={isVisibleToPublic}
						onChange={handleVisibilityToggle}
						disabled={isTogglingVisibility}
					/>
					<ToggleSlider
						htmlFor="visibility-toggle"
						$checked={isVisibleToPublic}
						$loading={isTogglingVisibility}
					/>
				</ToggleWrapper>
			</VisibilityRow>

			<ConfirmationModal
				isOpen={isConfirmOpen}
				onClose={() => setIsConfirmOpen(false)}
				onConfirm={handleConfirmDelete}
				title={isOwner === false ? 'Leave Trip' : 'Delete Trip'}
				message={
					isOwner === false
						? 'Are you sure you want to leave this trip? You will no longer have access.'
						: 'Are you sure you want to delete this trip? This action cannot be undone.'
				}
				confirmText={isOwner === false ? 'Leave' : 'Delete'}
				cancelText="Cancel"
				variant="danger"
			/>
		</Modal>
	)
}

export default EditTripModal
